import { useEffect, useMemo, useState } from "react";

import { LoadingSlide } from "@components/title-slide-view";
import {
  SummaryAccuracy,
  SummaryInfo,
  SummaryWeeklyAccuracy,
} from "@features/mmm/components/summary";
import { useMmmSummary } from "@features/mmm/components/summary/hooks/use-mmm-summary";
import { useNorthbeamQuery } from "@hooks/http";
import { type FetchBlessedBudgetMixesForCycle } from "@north-beam/nb-client-side";

import { BUDGET_MIXES_FOR_CYCLE_QUERY } from "../explore-budgets-v2/explore-budgets-queries";
import { useAdoptedMix } from "../hooks/use-adopted-mix";
import { useClientKpis } from "../hooks/use-client-kpis";
import { useClientSettingData } from "../hooks/use-client-setting-data";
import { useSelectedCycle } from "../hooks/use-selected-cycle";

export function SummaryTab() {
  const { kpis } = useClientKpis();

  if (!kpis.length) {
    return <LoadingSlide />;
  }
  return <SummaryContent />;
}
export function SummaryContent() {
  const { kpis } = useClientKpis();

  const { selectedCycle } = useSelectedCycle();
  const [adoptedMixData] = useAdoptedMix();
  const { loading, accuracyData } = useMmmSummary();

  const { data: budgetMixData, loading: budgetMixDataLoading } =
    useNorthbeamQuery<FetchBlessedBudgetMixesForCycle>(
      BUDGET_MIXES_FOR_CYCLE_QUERY,
      { variables: { id: selectedCycle.id } },
    );

  const { budgetMix, clientSetting } = useMemo(() => {
    if (!adoptedMixData || !budgetMixData) {
      return { budgetMix: undefined, clientSetting: undefined };
    }

    const matchingBudgetMix = budgetMixData.me.mmm.cycle.budgetMixes.find(
      (e) => e.id === adoptedMixData.id,
    );

    const matchingClientSettings =
      budgetMixData.me.mmm.cycle.clientSettings.find(
        (setting) => matchingBudgetMix?.client_setting_id === setting.id,
      );

    return {
      budgetMix: matchingBudgetMix,
      clientSetting: matchingClientSettings,
    } as const;
  }, [adoptedMixData, budgetMixData]);

  const { kpi: clientSettingKpi } =
    // @ts-ignore
    useClientSettingData(clientSetting);
  const mixKpi = useMemo(
    () =>
      kpis.find(
        (e) =>
          e.kpi_name === budgetMix?.data.kpi_name ||
          e.kpi_name === clientSettingKpi?.kpi_name,
      ),
    [budgetMix?.data.kpi_name, clientSettingKpi?.kpi_name, kpis],
  );

  const [selectedKpiId, setSelectedKpiId] = useState(kpis[0].id);

  const selectedKpi = useMemo(
    () => kpis.find((e) => e.id === selectedKpiId)!,
    [kpis, selectedKpiId],
  );

  useEffect(() => {
    if (mixKpi) {
      setSelectedKpiId(mixKpi.id);
    }
  }, [mixKpi]);

  const budgetMixSpend = useMemo(() => {
    if (!budgetMix) {
      return 0;
    }

    return budgetMix.data.aggregated_data.summary_data.spend;
  }, [budgetMix]);

  return (
    <div className="flex-1 flex flex-col space-y-4">
      {loading || budgetMixDataLoading ? (
        <div className="my-24">
          <LoadingSlide text="Hang tight, this could take a minute or two..." />
        </div>
      ) : !budgetMix ? (
        <div>No budget adopted</div>
      ) : (
        <>
          <SummaryInfo
            budgetMix={budgetMix}
            clientSetting={clientSetting}
            accuracyData={accuracyData}
            budgetMixSpend={budgetMixSpend}
            mixKpi={mixKpi}
          />
          <div className="border-b border-gray-2 -mx-5"></div>
          <SummaryAccuracy
            selectedKpi={selectedKpi}
            accuracyData={accuracyData}
            onChangeKpi={(value) => setSelectedKpiId(value.id)}
          />
          <SummaryWeeklyAccuracy
            accuracyData={accuracyData}
            selectedKpi={selectedKpi}
          />
        </>
      )}
    </div>
  );
}
